import React, { useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Stack,
  Badge,
  Button,
  Group,
  Box,
  Paper
} from '@mantine/core';
import {
  IconPlus,
  IconArrowsShuffle,
  IconX
} from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { useBusinessesQuery, useBusinessQuery, useCategoriesQuery, useReactivateBusinessMutation } from '../../../application/queries/businessQueries';
import { useRegionsQuery, useSubAreasQuery } from '../../../application/queries/regionQueries';
import { BusinessListItemDto } from '../../../domain/entities/Business';
import { CreateBusinessModal } from '../../components/domain/CreateBusinessModal';
import { EditBusinessModal } from '../../components/domain/EditBusinessModal';
import { DeactivateBusinessModal } from '../../components/domain/DeactivateBusinessModal';
import { BusinessesFilters } from '../../components/admin/Businesses/BusinessesFilters';
import { BusinessesList } from '../../components/admin/Businesses/BusinessesList';
import { ReassignBusinessesModal } from '../../components/admin/Businesses/ReassignBusinessesModal';

/**
 * BusinessesPage - Admin-only page for managing businesses
 * Sprint 045: Renamed delete → deactivate, added reactivate support
 * Sprint 053: Added bulk selection and reassign to company
 */
export const BusinessesPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Pagination state
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<string>('50'); // String for Select component

  // Search state - activeSearchTerm is what's actually being searched (after button click)
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>('');

  // Filter state
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null); // GUID for businesses filter
  const [selectedRegionStringId, setSelectedRegionStringId] = useState<string | null>(null); // String ID for SubAreas
  const [selectedSubAreaId, setSelectedSubAreaId] = useState<string | null>(null);

  // SESSION_320: Sort state
  const [sortBy, setSortBy] = useState<string>('Name');
  const [sortDescending, setSortDescending] = useState<boolean>(false);

  // Sprint 045: Active/Inactive filter
  const [selectedIsActive, setSelectedIsActive] = useState<boolean | undefined>(undefined);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState<boolean>(false);
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessListItemDto | null>(null);
  const [selectedBusinessIdForEdit, setSelectedBusinessIdForEdit] = useState<string | null>(null);

  // Sprint 053: Selection state for bulk operations
  const [selectedBusinessIds, setSelectedBusinessIds] = useState<Set<string>>(new Set());
  const [isReassignModalOpen, setIsReassignModalOpen] = useState<boolean>(false);

  // Sprint 045: Reactivate mutation
  const reactivateBusinessMutation = useReactivateBusinessMutation();

  // Fetch filter options
  const { data: categories } = useCategoriesQuery(true);
  const { data: regions } = useRegionsQuery({ pageNumber: 1, pageSize: 100 }, user?.role, true);
  const { data: subAreas } = useSubAreasQuery(selectedRegionStringId ?? undefined);

  // Fetch businesses using React Query with server-side pagination, search, filters, and sorting
  // SESSION_320: Added sortBy and sortDescending
  const { data, isLoading, error, isFetching } = useBusinessesQuery(
    { pageNumber, pageSize: parseInt(pageSize, 10) },
    user?.role,
    true,
    activeSearchTerm || undefined,
    selectedCategory || undefined,
    selectedRegionId || undefined,
    selectedSubAreaId || undefined,
    sortBy || undefined,
    sortDescending,
    selectedIsActive
  );

  const businesses = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  // Fetch full business details for edit modal
  const {
    data: fullBusinessForEdit,
    isLoading: isLoadingFullBusiness
  } = useBusinessQuery(
    selectedBusinessIdForEdit ?? '',
    user?.role ?? 'Admin',
    null,
    !!selectedBusinessIdForEdit && isEditModalOpen
  );

  /**
   * Calculate range for display (e.g., "Showing 1-50 of 6,988")
   */
  const displayRange = useMemo((): string => {
    if (totalCount === 0) return 'No businesses';
    const start = (pageNumber - 1) * parseInt(pageSize, 10) + 1;
    const end = Math.min(pageNumber * parseInt(pageSize, 10), totalCount);
    return `Showing ${start}-${end} of ${totalCount.toLocaleString()}`;
  }, [pageNumber, pageSize, totalCount]);

  /**
   * Sprint 053: Selection handlers
   */
  const handleToggleSelection = useCallback((id: string): void => {
    setSelectedBusinessIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectAllPage = useCallback((): void => {
    setSelectedBusinessIds(prev => {
      const allOnPage = businesses.map(b => b.id);
      const allSelected = allOnPage.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        // Deselect all on this page
        for (const id of allOnPage) {
          next.delete(id);
        }
      } else {
        // Select all on this page
        for (const id of allOnPage) {
          next.add(id);
        }
      }
      return next;
    });
  }, [businesses]);

  const isAllPageSelected = useMemo((): boolean => {
    if (businesses.length === 0) return false;
    return businesses.every(b => selectedBusinessIds.has(b.id));
  }, [businesses, selectedBusinessIds]);

  const handleClearSelection = useCallback((): void => {
    setSelectedBusinessIds(new Set());
  }, []);

  // Get full BusinessListItemDto objects for selected IDs (from current data)
  const selectedBusinessesForModal = useMemo((): BusinessListItemDto[] => {
    return businesses.filter(b => selectedBusinessIds.has(b.id));
  }, [businesses, selectedBusinessIds]);

  /**
   * Navigation handlers
   */
  const handleViewBusiness = useCallback(
    (business: BusinessListItemDto): void => {
      navigate(`/admin/businesses/${business.id}`);
    },
    [navigate]
  );

  const handleCreateBusiness = useCallback((): void => {
    setIsCreateModalOpen(true);
  }, []);

  const handleEditBusiness = useCallback((business: BusinessListItemDto): void => {
    setSelectedBusinessIdForEdit(business.id);
    setIsEditModalOpen(true);
  }, []);

  /**
   * Sprint 045: Open deactivate modal (renamed from delete)
   */
  const handleDeactivateBusiness = useCallback((business: BusinessListItemDto): void => {
    setSelectedBusiness(business);
    setIsDeactivateModalOpen(true);
  }, []);

  /**
   * Sprint 045: Reactivate business directly (no confirmation modal needed)
   */
  const handleReactivateBusiness = useCallback((business: BusinessListItemDto): void => {
    void reactivateBusinessMutation.mutateAsync({
      id: business.id,
      userRole: user?.role ?? 'Admin'
    });
  }, [reactivateBusinessMutation, user]);

  const handleSearch = useCallback((searchTerm: string): void => {
    setActiveSearchTerm(searchTerm);
    setPageNumber(1);
    setSelectedBusinessIds(new Set());
  }, []);

  const handleRegionChange = useCallback((regionGuid: string | null): void => {
    if (regionGuid && regions?.items) {
      const region = regions.items.find(r => r.id === regionGuid);
      setSelectedRegionId(regionGuid);
      setSelectedRegionStringId(region?.regionId ?? null);
    } else {
      setSelectedRegionId(null);
      setSelectedRegionStringId(null);
    }
    setSelectedSubAreaId(null);
    setPageNumber(1);
    setSelectedBusinessIds(new Set());
  }, [regions]);

  const handleSortChange = useCallback((newSortBy: string, newSortDescending: boolean): void => {
    setSortBy(newSortBy);
    setSortDescending(newSortDescending);
    setPageNumber(1);
  }, []);

  const handleClearFilters = useCallback((): void => {
    setActiveSearchTerm('');
    setSelectedCategory(null);
    setSelectedRegionId(null);
    setSelectedRegionStringId(null);
    setSelectedSubAreaId(null);
    setSelectedIsActive(undefined);
    setSortBy('Name');
    setSortDescending(false);
    setPageNumber(1);
    setSelectedBusinessIds(new Set());
  }, []);

  const hasActiveFilters = useMemo((): boolean => {
    return !!(activeSearchTerm || selectedCategory || selectedRegionId || selectedSubAreaId || selectedIsActive !== undefined);
  }, [activeSearchTerm, selectedCategory, selectedRegionId, selectedSubAreaId, selectedIsActive]);

  const handlePageChange = useCallback((newPage: number): void => {
    setPageNumber(newPage);
    setSelectedBusinessIds(new Set());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handlePageSizeChange = useCallback((value: string | null): void => {
    if (value) {
      setPageSize(value);
      setPageNumber(1);
      setSelectedBusinessIds(new Set());
    }
  }, []);

  const handleReassignComplete = useCallback((): void => {
    setSelectedBusinessIds(new Set());
    setIsReassignModalOpen(false);
  }, []);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Group gap="md" align="baseline">
            <Title order={1} ff="Comfortaa, sans-serif">
              Businesses
            </Title>
            {!isLoading && (
              <Badge size="xl" variant="light" color="blue">
                {totalCount.toLocaleString()} total
              </Badge>
            )}
          </Group>
          <Text c="dimmed" size="lg" mt="xs">
            Manage businesses and their information
          </Text>
        </Box>

        <Button leftSection={<IconPlus size={16} />} onClick={handleCreateBusiness}>
          Create Business
        </Button>
      </Group>

      {/* Search Bar and Filters */}
      <BusinessesFilters
        onSearch={handleSearch}
        categories={categories}
        regions={regions}
        subAreas={subAreas}
        selectedCategory={selectedCategory}
        onCategoryChange={(value): void => {
          setSelectedCategory(value);
          setPageNumber(1);
          setSelectedBusinessIds(new Set());
        }}
        selectedRegionId={selectedRegionId}
        onRegionChange={handleRegionChange}
        selectedRegionStringId={selectedRegionStringId}
        selectedSubAreaId={selectedSubAreaId}
        onSubAreaChange={(value): void => {
          setSelectedSubAreaId(value);
          setPageNumber(1);
          setSelectedBusinessIds(new Set());
        }}
        selectedIsActive={selectedIsActive}
        onIsActiveChange={(value): void => {
          setSelectedIsActive(value);
          setPageNumber(1);
          setSelectedBusinessIds(new Set());
        }}
        sortBy={sortBy}
        sortDescending={sortDescending}
        onSortChange={handleSortChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        displayRange={displayRange}
        isFetching={isFetching}
      />

      {/* Sprint 053: Bulk Action Toolbar */}
      {selectedBusinessIds.size > 0 && (
        <Paper
          shadow="sm"
          p="sm"
          radius="md"
          withBorder
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'var(--mantine-color-gray-0)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            borderBottom: '1px solid var(--mantine-color-gray-3)'
          }}
        >
          <Group justify="space-between">
            <Group gap="sm">
              <Badge size="lg" variant="filled" color="blue">
                {selectedBusinessIds.size} selected
              </Badge>
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconX size={14} />}
                onClick={handleClearSelection}
              >
                Clear Selection
              </Button>
            </Group>
            <Button
              size="sm"
              leftSection={<IconArrowsShuffle size={16} />}
              onClick={(): void => setIsReassignModalOpen(true)}
            >
              Reassign to Company
            </Button>
          </Group>
        </Paper>
      )}

      {/* Businesses List with Scoped Loading */}
      <BusinessesList
        businesses={businesses}
        totalCount={totalCount}
        totalPages={totalPages}
        isLoading={isLoading}
        error={error}
        pageNumber={pageNumber}
        onPageChange={handlePageChange}
        onViewBusiness={handleViewBusiness}
        onEditBusiness={handleEditBusiness}
        onDeactivateBusiness={handleDeactivateBusiness}
        onReactivateBusiness={handleReactivateBusiness}
        onCreateBusiness={handleCreateBusiness}
        searchQuery={activeSearchTerm}
        onClearSearch={(): void => setActiveSearchTerm('')}
        selectedIds={selectedBusinessIds}
        onToggleSelection={handleToggleSelection}
        onSelectAllPage={handleSelectAllPage}
        isAllPageSelected={isAllPageSelected}
      />

      {/* Create Business Modal */}
      <CreateBusinessModal
        isOpen={isCreateModalOpen}
        onClose={(): void => setIsCreateModalOpen(false)}
        onSuccess={(): void => {
          setIsCreateModalOpen(false);
        }}
      />

      {/* Edit Business Modal */}
      <EditBusinessModal
        isOpen={isEditModalOpen && !!fullBusinessForEdit && !isLoadingFullBusiness}
        business={fullBusinessForEdit ?? null}
        onClose={(): void => {
          setIsEditModalOpen(false);
          setSelectedBusinessIdForEdit(null);
        }}
        onSuccess={(): void => {
          setIsEditModalOpen(false);
          setSelectedBusinessIdForEdit(null);
        }}
      />

      {/* Deactivate Business Modal (Sprint 045: renamed from Delete) */}
      <DeactivateBusinessModal
        isOpen={isDeactivateModalOpen}
        business={selectedBusiness}
        onClose={(): void => {
          setIsDeactivateModalOpen(false);
          setSelectedBusiness(null);
        }}
        onSuccess={(): void => {
          setIsDeactivateModalOpen(false);
          setSelectedBusiness(null);
        }}
      />

      {/* Sprint 053: Reassign Businesses Modal */}
      <ReassignBusinessesModal
        isOpen={isReassignModalOpen}
        onClose={(): void => setIsReassignModalOpen(false)}
        selectedBusinesses={selectedBusinessesForModal}
        onReassignComplete={handleReassignComplete}
      />
    </Stack>
  );
};
